import React, { useEffect, Suspense, lazy } from 'react';
// Eager shell — always on screen, so keep it in the main bundle.
import { Header } from './components/layout/Header';
import { Navigation, PRIMARY_DESTINATION_IDS } from './components/layout/Navigation';
import { useReveal } from './hooks/useReveal';
import { resolveLandingView } from './lib/viewRouter';
import { DesktopXpToast } from './components/layout/DesktopXpToast';
import { CoinRewardHost } from './components/layout/CoinRewardToast';
import { ActionShell, ACTION_VIEWS } from './components/views/ActionShell';
import { LandingPage } from './components/LandingPage';
import { OnboardingFlow } from './components/Onboarding/OnboardingFlow';
import { LevelUpCelebrationManager } from './components/LevelUpCelebrationManager';
import { UnlockCelebrationManager } from './components/UnlockCelebrationManager';
import { CapacityBlockModal } from './components/CapacityBlockModal';
import { HabitDaysPrompt } from './components/HabitDaysPrompt';
import { ProfileStatsMirror } from './components/ProfileStatsMirror';
import { AIChat } from './components/AIChat';
import { HydrationLogPromptHost } from './components/disciplines/HydrationLogPromptHost';
import { NutritionLogPromptHost } from './components/disciplines/nutrition/NutritionLogPromptHost';
import { EatingHabitSync } from './components/disciplines/nutrition/EatingHabitSync';
import { SupplementsTickPromptHost } from './components/disciplines/supplements/SupplementsTickPromptHost';
import { SupplementsHabitSync } from './components/disciplines/supplements/SupplementsHabitSync';
import { PageGuideOverlay } from './components/guide/PageGuideOverlay';
import { BrandLoader } from './components/BrandLoader';
import { AccessPendingScreen } from './components/AccessPendingScreen';
import { supabase } from './lib/supabase';

// Route views are lazy-loaded so each splits into its own chunk and loads on
// demand — keeps the initial JS (and the native app's cold-start) small. The
// `.then(m => ({ default: m.X }))` form preserves each component's prop types.
const ScheduleView = lazy(() => import('./components/views/ScheduleView').then((m) => ({ default: m.ScheduleView })));
const TemplateEditor = lazy(() => import('./components/TemplateEditor').then((m) => ({ default: m.TemplateEditor })));
const TemplateList = lazy(() => import('./components/views/TemplatesView/TemplateList').then((m) => ({ default: m.TemplateList })));
const CurrentBlockView = lazy(() => import('./components/views/CurrentBlockView').then((m) => ({ default: m.CurrentBlockView })));
const InboxView = lazy(() => import('./components/views/InboxView').then((m) => ({ default: m.InboxView })));
const RoadmapView = lazy(() => import('./components/views/RoadmapView').then((m) => ({ default: m.RoadmapView })));
const TasksView = lazy(() => import('./components/views/TasksView').then((m) => ({ default: m.TasksView })));
const HabitsView = lazy(() => import('./components/views/HabitsView').then((m) => ({ default: m.HabitsView })));
const RoutinesView = lazy(() => import('./components/views/RoutinesView').then((m) => ({ default: m.RoutinesView })));
const StatusView = lazy(() => import('./components/views/StatusView').then((m) => ({ default: m.StatusView })));
const WalletView = lazy(() => import('./components/views/StatusView/WalletTab').then((m) => ({ default: m.WalletTab })));
const SettingsView = lazy(() => import('./components/views/SettingsView').then((m) => ({ default: m.SettingsView })));
const DomainView = lazy(() => import('./components/views/DomainView').then((m) => ({ default: m.DomainView })));
const ProjectsView = lazy(() => import('./components/views/ProjectsView').then((m) => ({ default: m.ProjectsView })));
const QuestsView = lazy(() => import('./components/views/QuestsView').then((m) => ({ default: m.QuestsView })));
const HomeView = lazy(() => import('./components/views/HomeView').then((m) => ({ default: m.HomeView })));
const AbilitiesView = lazy(() => import('./components/views/AbilitiesView').then((m) => ({ default: m.AbilitiesView })));
const ActionView = lazy(() => import('./components/views/ActionView').then((m) => ({ default: m.ActionView })));
const SocialView = lazy(() => import('./components/views/SocialView').then((m) => ({ default: m.SocialView })));
const GuildsView = lazy(() => import('./components/views/GuildsView').then((m) => ({ default: m.GuildsView })));
const GuildWorkspace = lazy(() => import('./components/views/GuildsView/GuildWorkspace').then((m) => ({ default: m.GuildWorkspace })));
const ProfileView = lazy(() => import('./components/views/ProfileView').then((m) => ({ default: m.ProfileView })));
const JournalView = lazy(() => import('./components/views/JournalView').then((m) => ({ default: m.JournalView })));
const MarketplaceView = lazy(() => import('./components/views/MarketplaceView').then((m) => ({ default: m.MarketplaceView })));
const ProgramsView = lazy(() => import('./components/views/ProgramsView').then((m) => ({ default: m.ProgramsView })));
const CalendarView = lazy(() => import('./components/views/CalendarView').then((m) => ({ default: m.CalendarView })));
const AssistantView = lazy(() => import('./components/views/AssistantView').then((m) => ({ default: m.AssistantView })));
const ExternalAssessmentPage = lazy(() => import('./components/views/ExternalAssessmentPage').then((m) => ({ default: m.ExternalAssessmentPage })));

// Brief fallback while a route chunk loads.
function ViewFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted-foreground border-t-transparent" />
    </div>
  );
}
import { useCurrentView } from './hooks/useCurrentView';
import { useViewUrlSync } from './hooks/useViewUrlSync';
import { useLevelUpDetection } from './hooks/useLevelUpDetection';
import { useLinkedGoalSync } from './hooks/useLinkedGoalSync';
import { useScheduleStore } from './stores/scheduleStore';
import { useTaskStore } from './stores/taskStore';
import { useHabitStore } from './stores/habitStore';
import { useEventStore } from './stores/eventStore';
import { useProjectStore } from './stores/projectStore';
import { useQuestStore } from './stores/questStore';
import { useJournalStore } from './stores/journalStore';
import { useRoutineStore } from './stores/routineStore';
import { useRoutineSessionLogStore } from './stores/routineSessionLogStore';
import { useCollaborationStore } from './stores/collaborationStore';
import { useCategoryStore } from './stores/categoryStore';
import { useSettingsStore } from './stores/settingsStore';
import { useNotificationPrefsStore } from './stores/notificationPrefsStore';
import { useTheme } from './hooks/useTheme';
import { useUserStore } from './stores/userStore';
import { useBreathworkStore } from './stores/breathworkStore';
import { useProgramStore } from './stores/programStore';
import { useGpbStore } from './stores/gpbStore';
import { useGpbCatalogStore } from './stores/gpbCatalogStore';
import { useSocialStore } from './stores/socialStore';
import { useFinanceStore } from './stores/financeStore';
import { useInboxStore } from './stores/inboxStore';
import { usePossessionsStore } from './stores/possessionsStore';
import { useIntellectualSubjectsStore } from './stores/intellectualSubjectsStore';
import { useExistentialPillarsStore } from './stores/existentialPillarsStore';
import { useAvocationalStore } from './stores/avocationalStore';
import { useResourcesStore } from './stores/resourcesStore';
import { useAcquisitionsStore } from './stores/acquisitionsStore';
import { useSyncedBlockStore } from './stores/syncedBlockStore';
import { useAIChatStore } from './stores/aiChatStore';
import { useLifeBalanceStore } from './stores/lifeBalanceStore';
import { useCoachStore } from './stores/coachStore';
import { useLocationStore } from './stores/locationStore';
import { useBodyAtlasStore } from './stores/bodyAtlasStore';
import { useHydrationStore } from './stores/hydrationStore';
import { useHomeWidgetsStore } from './stores/homeWidgetsStore';
import { useNutritionStore } from './stores/nutritionStore';
import { useNutritionPlanStore } from './stores/nutritionPlanStore';
import { useSupplementStore } from './stores/supplementStore';
import { useMedicationStore } from './stores/medicationStore';
import { useSleepStore } from './stores/sleepStore';
import { useRecoveryStore } from './stores/recoveryStore';
import { useHealthStore } from './stores/healthStore';
import { useCarePracticeStore } from './stores/carePracticeStore';
import { useFitnessStore } from './stores/fitnessStore';
import { useAbilitiesStore } from './stores/abilitiesStore';
import { useLanguageStore } from './stores/languageStore';
import { useOnboardingStore } from './stores/onboardingStore';
import { shouldAutoOpenOnboarding } from './utils/onboardingFlow';
import { useUpgradesStore } from './stores/upgradesStore';
import { useSync } from './hooks/useSync';
import { useAuthStore } from './stores/authStore';
import { useWalletStore } from './stores/walletStore';
import { useXpConfigStore } from './stores/xpConfigStore';
import { useGuildModeStore } from './stores/guildModeStore';
import { syncManager } from './lib/syncManager';

/**
 * The canvas height a page gets when it is the WHOLE view — no ribbon above it.
 *
 * ⚠️ A REAL height on purpose. The view wrapper below is
 * `lg:min-h-[calc(100vh-7rem)]`, and a percentage height inside a MIN-height
 * ancestor resolves against content, so `h-full` collapses there. Pages that
 * fill either carry this themselves (Roadmap, Calendar) or are handed it here.
 *
 * ⚠️ Used only by the pages that ALSO render as a Discover tab — Guilds and
 * Marketplace — which therefore cannot hardcode a budget of their own. The
 * ribboned counterpart is `DISCOVER_TAB_FILL` in `SocialView`.
 */
const STANDALONE_FILL = 'lg:h-[calc(100vh-7rem)] lg:min-h-0';

export default function App() {
  const { user, loading, checkAuth } = useAuthStore();
  /* ⚠️⚠️ THE USER-KEYED EFFECTS RUN ON THE ID, NOT THE OBJECT (owner, 2026-10-04). Signing
     in reports the user twice (session check, then onAuthStateChange) as two different
     objects for the SAME person, so `[user]` effects ran twice: measured on live, the
     boot loads that have no per-user guard of their own (finance ×8, social, collaboration,
     intellectual, existential, avocational) and check_app_access each went out twice. */
  const userId = user?.id ?? null;
  const { currentView, setCurrentView } = useCurrentView();
  // Defer the view that drives rendering so switching to a lazy route is treated as
  // a transition: React keeps the current view on screen until the next chunk loads
  // instead of throwing "suspended while responding to synchronous input". The nav
  // highlight still reads currentView directly, so it updates immediately.
  const renderView = React.useDeferredValue(currentView);

  /**
   * ⚠️⚠️ NEVER RENDER A DESTINATION THE RAIL WOULD NOT SHOW. `currentView` is
   * restored from `view-storage` and from the URL, and neither knows anything
   * about reveal — so on 2026-09-01 a signed-in user landed on Connect with a
   * rail beside it offering only Home. The nav filtered itself correctly; the
   * renderer simply never asked the same question.
   *
   * ⭐ It NAVIGATES rather than swapping what is painted, so the URL, the rail
   * highlight and the content agree. Rendering Home while the address bar still
   * said /discover would just move the inconsistency somewhere quieter.
   *
   * ⚠️ The fallback is the Roadmap (`LANDING_VIEW`), which is deliberately
   * never revealable — the Growth Module lives there — so it is always a safe
   * destination and this cannot bounce. Home is gated at step 4 since
   * 2026-09-29 and is no longer safe.
   *
   * ⚠️ Reveal fails OPEN while unhydrated, so a slow load never ejects someone
   * from a page they have genuinely unlocked.
   */
  const revealed = useReveal();
  useEffect(() => {
    if (!user) return;
    const target = resolveLandingView(currentView, PRIMARY_DESTINATION_IDS, revealed);
    if (target !== currentView) setCurrentView(target);
  }, [user, currentView, revealed, setCurrentView]);
  const inGuildMode = useGuildModeStore((s) => s.activeGuildId !== null);

  // Navigating anywhere outside the guilds view (drawer, notification
  // click-through, deep-link) always exits guild mode, so the guild rail
  // never strands on a foreign page.
  React.useEffect(() => {
    if (currentView !== 'guilds' && useGuildModeStore.getState().activeGuildId) {
      useGuildModeStore.getState().exitGuild();
    }
  }, [currentView]);
  const { language, direction } = useLanguageStore();
  const { initialize: initializeUpgrades } = useUpgradesStore();
  const {
    isOnboardingVisible,
    setOnboardingVisible,
    hasCompletedOnboarding,
    initializedUserId: onboardingCheckedFor,
  } = useOnboardingStore();
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);
  // Early-access gate: 'checking' until check_app_access() answers. Fails OPEN
  // (network/RPC error → granted) — the gate is a UX door, RLS still guards all
  // data; a flaky connection must never lock a real user out.
  const [accessGate, setAccessGate] = React.useState<'checking' | 'granted' | 'pending'>('checking');

  useEffect(() => {
    if (!userId) {
      setAccessGate('checking');
      return;
    }
    let cancelled = false;
    supabase
      .rpc('check_app_access')
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data === false) setAccessGate('pending');
        else setAccessGate('granted');
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    handleCreateTemplate,
    handleDuplicateTemplate,
    handleSaveTemplate,
    handleDeleteTemplate,
    getCurrentSchedule,
  } = useScheduleStore();

  // Keep the URL in sync with the current view (deep-links + back/forward)
  useViewUrlSync();

  // Initialize sync
  const { isOnline, error: syncError } = useSync();

  // Initialize level-up detection for standalone completions
  useLevelUpDetection('immediate');
  // Linked quest goals advance from wherever the action was actually completed.
  useLinkedGoalSync();

  // Initialize auth and sync manager
  useEffect(() => {
    checkAuth();
    syncManager.initialize().catch(err => {
      console.warn('Initial sync failed, continuing offline:', err);
    });
  }, [checkAuth]);

  // Loading timeout protection
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        console.warn('Loading timeout reached, forcing app to render');
        setLoadingTimeout(true);
      }, 12000);
      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [loading]);

  // Initialize upgrades system
  useEffect(() => {
    if (user) {
      initializeUpgrades();
    }
  }, [user, initializeUpgrades]);

  // Load tasks + habits + events + journal from Supabase on login (sync epic)
  useEffect(() => {
    if (userId) {
      useTaskStore.getState().initialize();
      useHabitStore.getState().initialize();
      useEventStore.getState().initialize();
      useJournalStore.getState().initialize();
      useRoutineStore.getState().initialize();
      // Load ALL routine-run history so useUserStats can roll its per-domain XP into
      // the real economy app-wide (not just when a routine's preview is opened).
      useRoutineSessionLogStore.getState().fetchSessions();
      // Collaboration XP from completing assigned tasks in shared projects — load
      // app-wide so useUserStats folds it into the domain totals (0217), same as
      // routine XP above. Fail-soft when the collab tables aren't migrated.
      useCollaborationStore.getState().fetchMyXp();
      useCategoryStore.getState().initialize();
      useLanguageStore.getState().initialize();
      useSettingsStore.getState().initialize();
      useNotificationPrefsStore.getState().initialize();
      useTheme.getState().initialize();
      useUserStore.getState().initialize();
      useBreathworkStore.getState().initialize();
      useQuestStore.getState().initialize();
      useProjectStore.getState().initialize();
      useAbilitiesStore.getState().initialize();
      useProgramStore.getState().initialize();
      /* ⚠️ Growth Profile enrollment: the binding that says which native record
         answers `H01` or `AP01-A00`. Every record it points AT syncs itself, so
         without this a second device shows an empty library beside real work. */
      useGpbStore.getState().initialize();
      /* ⚠️⚠️ The Growth Profile CATALOGUE — published releases, Official flags, the
         admin check and the shared drafts. Nothing loaded it on boot until
         2026-10-03: only the workspace's conflict buttons did, so the drafts list
         showed this browser's own saves, a published release reached nobody, and
         editor copies were keyed `anon:` (no user id) and orphaned on Discard. */
      useGpbCatalogStore.getState().initialize();
      /* ⚠️⚠️ The SOCIAL XP total, which the overall level sums. It loaded only when Social
         was opened, so a fresh browser showed the level without it (8 · 54%) and opening
         Social "added" XP earned long ago (9 · 20%) — read as XP for visiting (owner,
         2026-10-04). Loaded at startup now, and refreshed whenever social XP is awarded. */
      useSocialStore.getState().fetchSocialStats();
      /* ⚠️⚠️ FINANCE XP, the same way (owner, 2026-10-04): it is derived from your
         transactions, budgets and goals, which loaded only in the Finance tab — so the
         level lacked it everywhere else and jumped when Finance was opened. READ ONLY:
         `initializeUserFinance` (which CREATES default settings and categories) stays
         in the Finance tab — opening the app must never write finance rows. */
      void useFinanceStore.getState().loadFinanceData(userId);
      useScheduleStore.getState().initialize();
      useInboxStore.getState().initialize();
      useSyncedBlockStore.getState().initialize();
      useAIChatStore.getState().initialize();
      useOnboardingStore.getState().initialize();
      useLifeBalanceStore.getState().initialize();
      useCoachStore.getState().initialize();
      useLocationStore.getState().initialize();
      useBodyAtlasStore.getState().initialize();
      useHydrationStore.getState().initialize();
      // Home's domain pages (their widget layout, user_preferences.home_widgets).
      useHomeWidgetsStore.getState().initialize();
      useNutritionStore.getState().initialize();
      useNutritionPlanStore.getState().initialize();
      useSupplementStore.getState().initialize();
      useMedicationStore.getState().initialize();
      useSleepStore.getState().initialize();
      useRecoveryStore.getState().initialize();
      useHealthStore.getState().initialize();
      // Mind's care practices — app-wide so care XP counts from login, not only
      // once the Mind page mounts. initialize() is idempotent per user.
      useCarePracticeStore.getState().initialize();
      usePossessionsStore.getState().initialize();
      useResourcesStore.getState().initialize();
      useAcquisitionsStore.getState().initialize();
      useFitnessStore.getState().loadDirectXp();
      // Intellectual learning XP credits the domain app-wide (IN3) — fetch on load.
      useIntellectualSubjectsStore.getState().fetchSubjects();
      // Existential meaning XP (the pillar ledger) credits the domain app-wide (EX3).
      useExistentialPillarsStore.getState().fetchPillars();
      // Avocational hobby XP (Σ active hobbies) credits the domain app-wide — fetch
      // hobbies on load so the domain card is live even before visiting the domain.
      useAvocationalStore.getState().fetchHobbies();
      // Wallet — claims due Coin grants server-side and loads the balance.
      useWalletStore.getState().initialize();
      // XP economy config — hydrates XP constants from the console-managed
      // tables (falls back to hardcoded defaults when unreachable).
      useXpConfigStore.getState().initialize();
    }
  }, [userId]);

  /* ⭐ Signed out: forget the Home layout (and which Home page was open), so the next
     account never sees this one's widgets and Home opens on its main page again. */
  useEffect(() => {
    if (!userId) useHomeWidgetsStore.getState().clear();
  }, [userId]);

  /* Show the onboarding sequence to anyone signed in who has not finished it.
     ⚠️ The old flow gated this on `hasSeenWelcome` — a separate modal the user
     had to dismiss first. That modal is gone: it said nothing the sequence does
     not say better on its own first screen, and a dialog whose only job is to
     open another dialog is a step, not a welcome. */
  /* ⚠️⚠️ ONLY ONCE THE CLOUD HAS ANSWERED (live 2026-10-03). The local flag starts
     false in a fresh browser, so a returning user signing in on a new device got the
     whole sequence — and nothing hid it when `initialize()` then read
     `onboarding_completed = true`. Pressing through it re-runs `complete()`. */
  useEffect(() => {
    if (shouldAutoOpenOnboarding(user?.id, onboardingCheckedFor, hasCompletedOnboarding)) {
      setOnboardingVisible(true);
    }
  }, [user, onboardingCheckedFor, hasCompletedOnboarding, setOnboardingVisible]);

  if (loading && !loadingTimeout) {
    return <BrandLoader />;
  }

  if (loadingTimeout) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-foreground mb-2">Connection is taking longer than expected</h2>
          <p className="text-muted-foreground mb-6">We'll start the app now. Some features may be limited until the connection is established.</p>
          <button
            onClick={() => {
              setLoadingTimeout(false);
              useAuthStore.setState({ loading: false });
            }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity"
          >
            Continue to App
          </button>
        </div>
      </div>
    );
  }

  const path = window.location.pathname;
  const assessMatch = path.match(/^\/assess\/([a-f0-9-]+)$/i);
  if (assessMatch) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <ExternalAssessmentPage token={assessMatch[1]} />
      </Suspense>
    );
  }

  if (!user) {
    return <LandingPage />;
  }

  if (accessGate === 'checking') {
    return <BrandLoader />;
  }
  if (accessGate === 'pending') {
    return <AccessPendingScreen onGranted={() => setAccessGate('granted')} />;
  }

  function renderContent() {
    switch (renderView) {
      case 'domain': {
        const domainId = localStorage.getItem('selectedDomainId');
        return domainId ? <DomainView key={domainId} domainId={domainId} /> : null;
      }


      case 'inbox':
        return <InboxView />;

      case 'home':
        return <ScheduleView />;

      case 'calendar':
        return <CalendarView />;

      case 'assistant':
        return <AssistantView />;

      case 'templates':
        return selectedTemplate ? (
          <TemplateEditor
            template={selectedTemplate}
            onSave={handleSaveTemplate}
            onBack={() => setSelectedTemplate(null)}
          />
        ) : (
          <TemplateList
            templates={templates}
            onSelect={setSelectedTemplate}
            onDelete={handleDeleteTemplate}
            onCreate={handleCreateTemplate}
            onDuplicate={handleDuplicateTemplate}
          />
        );

      case 'habits':
        return <HabitsView />;

      case 'routines':
        return <RoutinesView />;

      case 'current':
        return (
          <CurrentBlockView
            schedule={getCurrentSchedule()}
            category={'personal'}
          />
        );

      case 'tasks':
        return <TasksView />;

      case 'projects':
        return <ProjectsView />;
        
      case 'quests':
        return <QuestsView />;
        
      case 'abilities':
        return <AbilitiesView />;
        
      case 'action':
        return <ActionView />;
        
      case 'discover':
        return <SocialView />;

      case 'guilds':
        // Inside a guild the canvas hosts the guild workspace (the nav rail
        // swaps to the guild's via GuildRail); otherwise the browse page.
        // ⚠️ GuildsView is `lg:h-full` because it is ALSO a Discover tab, which
        // has a ribbon this page does not — so the height is stated per mount
        // point. `STANDALONE_FILL` is a REAL height; the wrapper further down
        // this file is a MIN-height, under which `h-full` collapses.
        // GuildWorkspace has only this one mount point and keeps its own budget.
        return inGuildMode
          ? <GuildWorkspace />
          : <div className={STANDALONE_FILL}><GuildsView /></div>;

      case 'profile':
        return <ProfileView />;

      case 'journal':
        return <JournalView />;

      case 'roadmap':
        return <RoadmapView />;

      case 'marketplace':
        // ⚠️ Same as `guilds` above — also a Discover tab, so it is `lg:h-full`
        // and the caller supplies the budget.
        return <div className={STANDALONE_FILL}><MarketplaceView /></div>;

      case 'programs':
        return <ProgramsView />;

      case 'dashboard':
        return <HomeView />;

      case 'status':
        return <StatusView />;

      /* ⚠️ The same component that was the Status tab, now the whole page. It
         renders a plain `space-y-4` stack and needs no wrapper — the tab never
         gave it one either. */
      case 'wallet':
        return <WalletView />;

      case 'settings':
        return <SettingsView />;

      // ⚠️ An unknown view id lands on the Roadmap, not Home: Home is gated
      // until step 4 (2026-09-29), so a bad id must not open a hidden page.
      default:
        return <RoadmapView />;
    }
  }

  return (
    <div className={`min-h-screen bg-background ${direction}`}>
      <Header />
      <div className="flex">
        <Navigation />
        <main className="flex-1 p-4 lg:p-6 lg:h-[calc(100vh-4rem)] overflow-auto scroll-shadow pb-28 lg:pb-6">
          {/* Sync Status */}
          {!isOnline && (
            <div className="mb-4 p-2 bg-amber-500/10 text-amber-500 text-sm rounded-md flex items-center justify-center">
              You're offline. Changes will sync when you reconnect.
            </div>
          )}
          {syncError && (
            <div className="mb-4 p-2 bg-destructive/10 text-destructive text-sm rounded-md flex items-center justify-center">
              {syncError}
            </div>
          )}
          <div className="max-w-[1800px] mx-auto lg:min-h-[calc(100vh-7rem)]">
            <Suspense fallback={<ViewFallback />}>
              {ACTION_VIEWS.has(renderView) ? (
                <ActionShell currentView={renderView}>{renderContent()}</ActionShell>
              ) : (
                renderContent()
              )}
            </Suspense>
          </div>
        </main>
      </div>
      {/* ⚠️ The sequence marks itself complete (it has consents to record and a
          profile to write first), so there is no onClose that flips the flag
          behind its back — that is how a half-finished onboarding used to count
          as done. */}
      {isOnboardingVisible && <OnboardingFlow />}
      <LevelUpCelebrationManager />
      <UnlockCelebrationManager />
      {/* ⚠️ MOUNTED ONCE, HERE. Ten create paths can raise a capacity block, and
          a modal rendered beside each of them would be ten copies free to drift
          — and would unmount with the form that opened it. It renders nothing
          until `capacityBlockStore.block` is set. */}
      <CapacityBlockModal />
      {/* ⚠️ MOUNTED ONCE, like the capacity modal: the AI coach, quest proposals and
          imports ask for a habit's days from code with no UI of its own. */}
      <HabitDaysPrompt />
      <DesktopXpToast />
      {/* ⚠️ MOUNTED ONCE: it holds the realtime ear on the user's own ledger,
          which has to listen whether or not a card is showing. */}
      <CoinRewardHost />
      <ProfileStatsMirror />
      <AIChat />
      <HydrationLogPromptHost />
      <NutritionLogPromptHost />
      <EatingHabitSync />
      <SupplementsTickPromptHost />
      <SupplementsHabitSync />
      <PageGuideOverlay />
    </div>
  );
}